import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Dispatch, RootState } from "shared/store/store";
import * as Yup from "yup";
import { FormikProps } from "formik";
import { useDetailedConsultation } from "pages/detailed-consultation/hooks/useDetailedConsultation";
import { GET_ELECTRONIC_DOCS } from "shared/api";
import Swal from "sweetalert2";

export const usePrepareInvoice = (
  isHadleSubmit: boolean,
  setIsHadleSubmit: any,
  setIsNextPage: any,
) => {
  const { id } = useParams();
  const dispatch = useDispatch<Dispatch>();
  const prepareInvoiceData = useSelector(
    (state: RootState) => state.prepareInvoiceData,
  );
  const linkDocument = useSelector((state: RootState) => state.linkDocument);
  const { departments } = useSelector(
    (state: RootState) => state.optionsDropdowns,
  );
  const [payLine, setPayLine] = useState("");
  const [isPaidValue, setIsPaidValue] = useState(true);
  const [isUnderage, setIsUnderage] = useState(false);
  const [document, setDocument] = useState<any>(null);
  const [cities, setCities] = useState(false);
  const formikRef = useRef<FormikProps<any>>(null);
  const typeAccept = ["PDF"];
  const [isLoading, setIsLoading] = useState(false);
  const { callGetDetailedDocument } = useDetailedConsultation();
  const codesMinors = ["42", "46", "44", "50", "53"];

  useEffect(() => {
    Object.keys(prepareInvoiceData).length === 0 &&
      dispatch.prepareInvoiceData.getInvoiceDataAsync({ id });
    dispatch.optionsDropdowns.getUserTypeDocuments();

    return () => {
      dispatch.prepareInvoiceData.clearInvoiceData();
      dispatch?.linkDocument?.cleanState();
    };
  }, []);

  useEffect(() => {
    if (isHadleSubmit) {
      formikRef.current?.submitForm();
      setIsHadleSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHadleSubmit]);

  useEffect(() => {
    if (departments.length === 0) {
      dispatch.optionsDropdowns.getDepartments();
    }
  }, [departments?.length == 0]);

  useEffect(() => {
    if (Object.keys(prepareInvoiceData).length > 0) {
      formikRef.current?.setValues({
        recoveryValue: prepareInvoiceData?.recoveryFeeValue,
        paidValue: prepareInvoiceData.valuePaidByTheUser,
        typeDocument: !prepareInvoiceData.isUnderage
          ? prepareInvoiceData.typeDocumentPatientSeen
          : "",
        documentNumber: !prepareInvoiceData.isUnderage
          ? prepareInvoiceData.documentNumberPatientSeen
          : "",
        names: !prepareInvoiceData.isUnderage
          ? prepareInvoiceData.namePatientSeen
          : "",
        lastNames: !prepareInvoiceData.isUnderage
          ? prepareInvoiceData.LastNamePatientAttended
          : "",
        tel1: prepareInvoiceData.phoneOne,
        tel2: prepareInvoiceData.phoneTwo,
        email: prepareInvoiceData.email,
        address: prepareInvoiceData.address,
        department: prepareInvoiceData.province,
        city: prepareInvoiceData.city,
        namePatientSeen: prepareInvoiceData.namePatientSeen,
        LastNamePatientAttended: prepareInvoiceData.LastNamePatientAttended,
        payLine: prepareInvoiceData.paymentLineId,
        isLoadFile: prepareInvoiceData.file.id ? true : null,
        typeDocumentPatientSeen: prepareInvoiceData.typeDocumentPatientSeen,
        documentNumberPatientSeen: prepareInvoiceData.documentNumberPatientSeen,
      });
      prepareInvoiceData.paymentLineId &&
        setPayLine(prepareInvoiceData.paymentLineId);
      prepareInvoiceData.province &&
        dispatch.optionsDropdowns.getCities(prepareInvoiceData.province);
      setIsPaidValue(prepareInvoiceData.isRecoveryFee);
      setIsUnderage(prepareInvoiceData.isUnderage);
      if (prepareInvoiceData.file.id) {
        const file = new File(
          [prepareInvoiceData.file.originalName],
          prepareInvoiceData.file.originalName,
          { type: "text/plain" },
        );
        dispatch.linkDocument.getLink({
          id: prepareInvoiceData.file.id,
          originalName: prepareInvoiceData.file.originalName,
          link: prepareInvoiceData.file.path,
        });
        setDocument(file);
      }
    }
  }, [prepareInvoiceData]);

  const handleChangeRadioPaidValue = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value === "false") {
      Swal.fire({
        title: "Atención",
        text: "Se recuerda que es responsabilidad del prestador realizar la gestión de cobro de la cuota de recuperación, de acuerdo con la normatividad vigente. Esta información es clave para los procesos de seguimiento y auditoría.",
        icon: "warning",
        showCancelButton: false,
        confirmButtonText: "Aceptar",
      });
    } else {
      Swal.fire({
        title: "Atención",
        text: "Recuerde que si se cobró cuota de recuperación, esta debe reportarse en el XML de la factura como un descuento global. No debe incluirse como anticipo, ya que La Secretaría de Salud e inclusión social de Antioquia no realiza anticipos a las facturas por prestación de servicios de salud.",
        icon: "info",
        showCancelButton: false,
        confirmButtonText: "Aceptar",
      });
    }

    setIsPaidValue((event.target as HTMLInputElement).value === "true");
    formikRef.current?.setValues((values: any) => {
      return {
        ...values,
        paidValue: "0",
      };
    });
  };

  const handleChangeRadioUnderage = (value: any) => {
    const underage = codesMinors.includes(value?.target?.value || value);
    setIsUnderage(underage);
    if (!underage) {
      formikRef.current?.setValues((values: any) => {
        return {
          ...values,
          names: values.namePatientSeen,
          lastNames: values.LastNamePatientAttended,
          documentNumber: values.documentNumberPatientSeen,
          typeDocument: value.target.value,
          typeDocumentPatientSeen: value.target.value,
        };
      });
    } else {
      formikRef.current?.setValues((values: any) => {
        return {
          ...values,
          names: "",
          lastNames: "",
          documentNumber: "",
          typeDocument: "",
          typeDocumentPatientSeen: value.target.value,
        };
      });
    }
  };

  const onlyNumbers = (event: any) => {
    const text = (event.clipboardData || window.Clipboard).getData("text");

    if (!/^(\d+)$/.test(text)) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  const validationSchema = useMemo(() => {
    if (
      formikRef.current?.values.recoveryValue !==
      formikRef.current?.values.paidValue
    ) {
      return () =>
        Yup.object().shape({
          payLine: Yup.string().trim().required(),
          typeDocumentPatientSeen: Yup.string().trim().required().max(50),
          documentNumberPatientSeen: Yup.string().trim().required().max(50),
          namePatientSeen: Yup.string().max(50).trim().required(),
          LastNamePatientAttended: Yup.string().max(50).trim().required(),
          recoveryValue: Yup.string().trim().max(14).required(),
          paidValue: Yup.string().trim().max(14).required(),
          isLoadFile: Yup.boolean().required(),
          typeDocument: Yup.string().trim().required().max(50),
          documentNumber: Yup.string().trim().required().max(100),
          names: Yup.string().trim().required().max(50),
          lastNames: Yup.string().trim().required().max(50),
          tel1: Yup.string().trim().required().max(10),
          tel2: Yup.string().trim().max(10).required(),
          email: Yup.string().trim().email().max(50).required(),
          address: Yup.string().trim().max(50).required(),
          department: Yup.string().trim().required(),
          city: Yup.string().trim().required(),
        });
    } else {
      return () =>
        Yup.object().shape({
          payLine: Yup.string().trim().required(),
          typeDocumentPatientSeen: Yup.string().trim().required().max(50),
          documentNumberPatientSeen: Yup.string().trim().required().max(50),
          namePatientSeen: Yup.string().max(50).trim().required(),
          LastNamePatientAttended: Yup.string().max(50).trim().required(),
          recoveryValue: Yup.string().trim().max(14).required(),
          paidValue: Yup.string().trim().max(14).required(),
          isLoadFile: Yup.boolean().required(),
        });
    }
  }, [
    formikRef.current?.values.recoveryValue,
    formikRef.current?.values.paidValue,
  ]);

  const handleChangeSelect = (event: any) => {
    setPayLine(event.target.value);
    formikRef.current?.setValues((values: any) => ({
      ...values,
      payLine: event.target.value,
    }));
  };

  const handleBlurSelectDepartment = (event: any) => {
    const { value } = event.target;
    formikRef.current?.setValues((values: any) => ({
      ...values,
      city: "",
    }));
    dispatch.optionsDropdowns.addCities([]);

    if (!value) return;

    dispatch.optionsDropdowns?.getCities(value);
    setCities(!cities);
  };

  const handleSubmit = (values: any) => {
    if (!linkDocument.id) {
      dispatch.messageModal.setMessageModal({
        show: true,
        title: "Archivo requerido",
        description: "Debe subir un archivo válido antes de enviar.",
        onClose() {
          dispatch.messageModal.cleanStateMessage();
        },
      });
      return;
    }
    dispatch.electronicDocuments
      .putPrepareInvoiceAsync({
        url: `${id}/prepare`,
        params: {
          paymentLineId: payLine,
          patientTypeId: values.typeDocumentPatientSeen,
          patientDocumentNumber: values.documentNumberPatientSeen,
          isRecoveryFee: isPaidValue,
          electronicDocument: id,
          recoveryFeeValue: Number(values.recoveryValue.replace(/\D/g, "")),
          valuePaidByTheUser: Number(values.paidValue.replace(/\D/g, "")),
          documentType: !isUnderage ? values.typeDocumentPatientSeen : "",
          documentNumber: !isUnderage ? values.documentNumberPatientSeen : "",
          name: !isUnderage ? values.namePatientSeen : "",
          lastname: !isUnderage ? values.LastNamePatientAttended : "",
          phoneOne: "+57" + values.tel1,
          phoneTwo: "+57" + values.tel2,
          email: values.email,
          province: values.department,
          city: values.city,
          address: values.address,
          patientName: values.namePatientSeen, // campo nuevo
          patientLastName: values.LastNamePatientAttended, // campo nuevo
          isYounger: isUnderage, // campo nuevo
          attachmentIds: [linkDocument.id],
        },
      })
      ?.then(() => {
        callGetDetailedDocument(id || ""); // *Llamar cuando se obtenga la respuesta correcta del servicio
        setIsNextPage(true); // * Llamar cuando se obtenga la respuesta correcta del servicio
      });
  };

  const handleChangeFile = (file: any) => {
    if (file && file.size < 10000000 && file.name.split(".").pop() === "pdf") {
      setIsLoading(true);
      formikRef.current?.setValues((values: any) => ({
        ...values,
        isLoadFile: true,
      }));
      dispatch.linkDocument
        .getLinkAsync({
          file: file,
          url: `${GET_ELECTRONIC_DOCS}/${id}/generate-upload-link`,
        })
        .then(() => {
          setDocument(file);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const removeFile = () => {
    setDocument(null);
    formikRef.current?.setValues((values: any) => ({
      ...values,
      isLoadFile: null,
    }));
  };

  return {
    id,
    payLine,
    isPaidValue,
    document,
    typeAccept,
    formikRef,
    cities,
    isUnderage,
    prepareInvoiceData,
    handleChangeRadioUnderage,
    handleChangeRadioPaidValue,
    handleBlurSelectDepartment,
    handleChangeSelect,
    handleSubmit,
    validationSchema,
    handleChangeFile,
    removeFile,
    onlyNumbers,
    codesMinors,
    isLoading,
  };
};
